import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocaleId } from '../models/locale.model';
import { ReportRecord } from '../models/report.model';
import { CustomDataRetriever } from './custom-data-retriever.abstract';
import { CustomDataTable } from './offered-space.service';

interface StaticResearchAvailabilityRecord {
  propertyId: string;
  address: string;
  zipcode: string;
  market: string;
  marketGroup: string;
  floor: string;
  floorSplit: string;
  status: string;
  total: string;
  tenure: string;
  leaseType: string;
  term: string;
  leaseExpiry: string;
  rent: string;
  rates: string;
  serviceCharge: string;
  price: string;
  spec: string;
  qualitySpace: string;
  agentCompanyData: string;
}

@Injectable()
export class ResearchAvailabilityService extends CustomDataRetriever {
  constructor(private http: HttpClient) {
    super();
  }

  getCustomData(
    reportId: number,
    _templateId: number,
    _locale: LocaleId,
    payload: any,
    record: ReportRecord
  ): Observable<StaticResearchAvailabilityRecord> {
    const researchAvailData$ = this.getResearchAvailabilityData(reportId, payload);
    return researchAvailData$.pipe(map(researchAvailData => this.mapResearchAvail(researchAvailData, record)));
  }

  private getResearchAvailabilityData(reportId, payload): Observable<CustomDataTable> {
    const uri = `/api/reports/${reportId}/customdata?type=researchAvailability&view=researchAvailability`;
    return this.cachedCustomData(uri, this.http.post.bind(this.http, uri, payload));
  }

  private mapResearchAvail(researchAvailData: CustomDataTable, record: ReportRecord): StaticResearchAvailabilityRecord {
    if (researchAvailData == null) {
      return null;
    }
    const { edpRecordId } = record;
    const recordResearchAvailability = researchAvailData[edpRecordId];

    if (recordResearchAvailability == null) {
      return null;
    }

    const { data } = recordResearchAvailability;
    if (Array.isArray(data) && data.length === 0 && data.length > 1) {
      throw new Error('data must not be empty or greater than one element per property');
    } else {
      const [row] = data;
      if (!Array.isArray(row) || row.length !== 20) {
        throw new Error('row data must be an array of 20 values/attributes');
      }
      /*
       * NOTE 1: this mapping should not be happening, but we are using objects
       *         due to windward not supporting the original interface
       * NOTE 2: we have two scenarios: longlist & shortlist. The longlist
       *         only needs the **four** attributes we are sending in the "if"
       *         block. However, the shortlist needs the **eight** attributes we
       *         are sending in the "else" block.
       */
      const [
        propertyId,
        address,
        zipcode,
        market,
        marketGroup,
        floor,
        floorSplit,
        status,
        total,
        tenure,
        leaseType,
        term,
        leaseExpiry,
        rent,
        rates,
        serviceCharge,
        price,
        spec,
        qualitySpace,
        agentCompanyData,
      ] = row;
      return {
        propertyId,
        address,
        zipcode,
        market,
        marketGroup,
        floor,
        floorSplit,
        status,
        total,
        tenure,
        leaseType,
        term,
        leaseExpiry,
        rent,
        rates,
        serviceCharge,
        price,
        spec,
        qualitySpace,
        agentCompanyData,
      };
    }
  }
}
